import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/core/AuthContext';
import { motion } from 'framer-motion';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ALERTS } from '../styles/classNames';

/**
 * Debug page with test login options
 * WARNING: This page is for development/testing only
 * Do not link to this page from production UI
 */
export function Debug() {
  const { signInWithGoogle, signInAsTrainer, userData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userData) return;

    if (userData.role === 'trainer') {
      navigate('/trainer', { replace: true });
    } else if (userData.role === 'client') {
      navigate('/client', { replace: true });
    }
  }, [userData, navigate]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      await signInWithGoogle();
      // Navigation will happen automatically via the effect above
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleAiTrainerSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      // The secret token for the AI trainer (default-coach)
      // This should match what's set up in Firebase Functions
      await signInAsTrainer('ai-trainer-default-coach');
      // Navigation will happen automatically via the effect above
    } catch (err: any) {
      setError(err.message || 'Failed to sign in as AI trainer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-900">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <Card variant="elevated" padding="lg" className="shadow-2xl border-2 border-yellow-500">
          {/* Warning Banner */}
          <div className="mb-6 p-4 bg-yellow-100 dark:bg-yellow-900/30 border-2 border-yellow-500 rounded-lg">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-sm font-bold text-yellow-900 dark:text-yellow-200">
                  DEBUG MODE - Development Only
                </p>
                <p className="text-xs text-yellow-800 dark:text-yellow-300 mt-1">
                  This page is for testing only. Do not share this URL.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mb-8">
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="text-4xl font-bold mb-2 text-yellow-500"
            >
              Debug Console
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-gray-600 dark:text-slate-400 text-lg"
            >
              Test Login Options
            </motion.p>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`mb-6 text-sm ${ALERTS.error}`}
            >
              {error}
            </motion.div>
          )}

          {/* Google Sign-In */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mb-4"
          >
            <p className="text-xs text-gray-500 dark:text-slate-500 mb-2 font-semibold">
              GOOGLE AUTH (DEV)
            </p>
            <Button
              onClick={handleGoogleSignIn}
              disabled={loading}
              isLoading={loading}
              variant="secondary"
              size="lg"
              className="w-full"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {loading ? 'Signing in...' : 'Sign in with Google'}
            </Button>
          </motion.div>

          <div className="my-6 relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-slate-600" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-slate-800 text-gray-500 dark:text-slate-400">
                or
              </span>
            </div>
          </div>

          {/* AI Trainer Login */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-4"
          >
            <p className="text-xs text-gray-500 dark:text-slate-500 mb-2 font-semibold">
              AI TRAINER (DEV)
            </p>
            <Button
              onClick={handleAiTrainerSignIn}
              disabled={loading}
              isLoading={loading}
              variant="ghost"
              size="md"
              className="w-full"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Login as AI Trainer
            </Button>
          </motion.div>

          {/* Test Account Links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-6 pt-6 border-t border-gray-200 dark:border-slate-700"
          >
            <p className="text-xs text-gray-500 dark:text-slate-500 mb-3 font-semibold">
              E2E TEST ACCOUNTS
            </p>
            <div className="space-y-2">
              <a
                href="/t/Wt2XSXpPHiNFgH2xcVaWGekfO16L5zm6"
                className="block w-full px-4 py-2 text-sm text-center text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-500/10 hover:bg-primary-100 dark:hover:bg-primary-500/20 rounded-lg transition-colors"
              >
                Sign in as Trainer (Test)
              </a>
              <a
                href="/c/b47e409083c34ce8b716fb5754b97d51"
                className="block w-full px-4 py-2 text-sm text-center text-accent-600 dark:text-accent-400 bg-accent-50 dark:bg-accent-500/10 hover:bg-accent-100 dark:hover:bg-accent-500/20 rounded-lg transition-colors"
              >
                Sign in as Client (Test)
              </a>
            </div>
          </motion.div>

          {/* Debug Tools */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-6 pt-6 border-t border-gray-200 dark:border-slate-700"
          >
            <p className="text-xs text-gray-500 dark:text-slate-500 mb-3 font-semibold">
              DEBUG TOOLS
            </p>
            <a
              href="/debug/screenshots"
              className="block w-full px-4 py-2 text-sm text-center text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
            >
              UX Screenshots Gallery
            </a>
          </motion.div>

          {/* Back to Login */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-6 text-center"
          >
            <a
              href="/login"
              className="text-sm text-gray-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              ← Back to Login
            </a>
          </motion.div>
        </Card>
      </motion.div>
    </div>
  );
}
